<template>
    <q-card
        class="producto-card"
        v-element-hover="onHover"
        :class="{ 'producto-card--elevated': isHovered }"
        ripple
    >
        <!-- Contenedor de imagen con overlay en hover -->
        <div class="producto-card__img-wrapper">
            <q-img
                :src="imgSrc"
                alt="product image"
                fit="contain"
                loading="lazy"
                :ratio="1"
                class="producto-card__img"
            >
                <template v-slot:error>
                    <div class="full-width full-height column items-center justify-center bg-grey-2">
                        <q-icon name="broken_image" size="48px" color="grey-5" />
                        <span class="text-caption text-grey-6 q-mt-xs">Sin imagen</span>
                    </div>
                </template>

                <template v-slot:loading>
                    <div class="full-width full-height column items-center justify-center">
                        <q-spinner-dots color="primary" size="32px" />
                    </div>
                </template>
            </q-img>

            <!-- Acciones rápidas en hover -->
            <transition name="slide-fade">
                <div v-show="isHovered" class="producto-card__quick-actions">
                    <!-- <q-btn
                        round
                        dense
                        flat
                        icon="visibility"
                        color="white"
                        size="sm"
                        @click.stop="$emit('quick-view')"
                    >
                        <q-tooltip anchor="top middle" self="bottom middle">
                            Vista rápida
                        </q-tooltip>
                    </q-btn> -->
                    <q-btn
                        round
                        dense
                        flat
                        icon="favorite_border"
                        color="white"
                        size="sm"
                        @click.stop="$emit('toggle-favorite')"
                    >
                        <q-tooltip anchor="top middle" self="bottom middle">
                            Favoritos
                        </q-tooltip>
                    </q-btn>
                </div>
            </transition>

            <!-- Badge de grupo -->
            <q-badge
                v-if="grupo"
                :style="`background-color: ${useStringToColor(grupo)};`"
                class="producto-card__badge-grupo"
                text-color="white"
            >
                {{ grupo }}
            </q-badge>

            <!-- Badge de stock (opcional) -->
            <q-badge
                v-if="showStock"
                :color="stockColor"
                class="producto-card__badge-stock"
                text-color="white"
            >
                {{ stockLabel }}
            </q-badge>
        </div>

        <!-- Contenido mejorado -->
        <q-card-section class="producto-card__body">
            <div class="producto-card__title-wrapper">
                <h3 class="producto-card__title">{{ titulo }}</h3>
            </div>

            <p class="producto-card__desc">{{ descripcion }}</p>

            <div class="producto-card__footer">
                <span class="producto-card__price">
                    {{ numberFormat(precio) }}
                </span>
                <div class="producto-card__action-icon">
                    <q-btn round dense flat @click.stop="handleCardClick">
                        <q-icon name="add_shopping_cart" size="22px" color="primary" />
                    </q-btn>
                </div>
            </div>
        </q-card-section>

    </q-card>
</template>

<script setup>
import numberFormat from "@/helpers/numberFormat";
import { computed, shallowRef } from "vue";
import { usePage } from '@inertiajs/vue3'
import { vElementHover } from "@vueuse/components";
import { useStringToColor } from "@/composables/useStringToColor";

const page = usePage()
const isHovered = shallowRef(false);
const onHover = (state) => (isHovered.value = state);

const props = defineProps({
    titulo: [String],
    descripcion: [String],
    grupo: [String],
    id: [String, Number],
    precio: [Number, String],
    imagenesPath: [String],
    stock: {
        type: Number,
        default: null
    },
    showStock: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(["add", "quick-view", "toggle-favorite"]);

const imgSrc = computed(() => `${props.imagenesPath}/${props.titulo}.webp`);
const noImagePath = page.props.noImagePath;

const handleCardClick = () => {
    emit('add');
};

// Lógica de stock
const stockColor = computed(() => {
    if (!props.stock) return 'grey';
    if (props.stock > 10) return 'positive';
    if (props.stock > 0) return 'warning';
    return 'negative';
});

const stockLabel = computed(() => {
    if (!props.stock || props.stock === 0) return 'Sin stock';
    if (props.stock <= 5) return 'Poco stock';
    return 'Disponible';
});
</script>
<style lang="scss" scoped>
.producto-card {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: white;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
        border-color: var(--q-primary);
    }

    &--elevated {
        transform: translateY(-8px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    }

    &__img-wrapper {
        position: relative;
        overflow: hidden;
        background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
        height: 220px;
    }

    &__img {
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

        .producto-card:hover & {
            transform: scale(1.05);
        }
    }

    &__quick-actions {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        gap: 12px;
        z-index: 10;

        &::before {
            content: '';
            position: absolute;
            inset: -10px;
            background: rgba(0, 0, 0, 0.4);
            backdrop-filter: blur(8px);
            border-radius: 50px;
            z-index: -1;
        }
    }

    &__badge-grupo {
        position: absolute;
        top: 12px;
        right: 12px;
        border-radius: 8px;
        padding: 4px 12px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &__badge-stock {
        position: absolute;
        top: 12px;
        left: 12px;
        border-radius: 8px;
        padding: 4px 12px;
        font-size: 0.7rem;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &__body {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: 16px;
        gap: 8px;
    }

    &__title-wrapper {
        min-height: 48px;
        display: flex;
        align-items: start;
    }

    &__title {
        font-size: 1rem;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__desc {
        font-size: 0.875rem;
        color: #6b7280;
        margin: 0;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: auto;
        padding-top: 12px;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    &__price {
        font-size: 1.25rem;
        font-weight: 700;
        color: var(--q-primary);
        letter-spacing: -0.5px;
    }

    &__action-icon {
        transition: transform 0.2s ease;

        .producto-card:hover & {
            transform: scale(1.1);
        }
    }
}

// Animaciones
.slide-fade-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, -40%);
}

// Responsive
@media (max-width: 768px) {
    .producto-card {
        &__img-wrapper {
            height: 180px;
        }

        &__title {
            font-size: 1.5rem;
        }

        &__desc {
            font-size: 0.8rem;
        }

        &__price {
            font-size: 1.1rem;
        }
    }
}
</style>
